import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { adminService, type ApprovalRow } from '../../services/adminService'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { formatShortDay } from '../../utils/format'

const STATUS_TABS = [
  { value: 'pending_approval', label: 'Awaiting approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'pending_verification', label: 'Unconfirmed' },
]

/**
 * Public self-registration approval queue. Admins review registrations whose
 * email is confirmed (pending_approval), then approve (emails the registrant an
 * app sign-in link, subject to capacity) or reject them.
 */
export default function RegistrationApprovalsManager({
  eventId,
}: {
  eventId: string
}) {
  const { notify } = useToast()
  const [status, setStatus] = useState('pending_approval')
  const [rows, setRows] = useState<ApprovalRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(
    (s: string) => {
      setLoading(true)
      adminService
        .listRegistrationApprovals(eventId, s)
        .then(setRows)
        .catch((e) => notify(apiError(e), 'error'))
        .finally(() => setLoading(false))
    },
    [eventId, notify],
  )

  useEffect(() => {
    load(status)
  }, [status, load])

  const act = async (row: ApprovalRow, action: 'approve' | 'reject') => {
    setBusy(row.registrationId)
    try {
      if (action === 'approve') {
        await adminService.approveRegistration(eventId, row.registrationId)
        notify(`Approved ${row.email} — access link emailed.`, 'success')
      } else {
        await adminService.rejectRegistration(eventId, row.registrationId)
        notify(`Rejected ${row.email}.`, 'success')
      }
      load(status)
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Registration approvals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Public self-registrations awaiting review. Approving emails the person a
        secure link to access the event and mobile app.
      </Typography>

      <Tabs
        value={status}
        onChange={(_, v) => setStatus(v)}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {STATUS_TABS.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={t.label}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          />
        ))}
      </Tabs>

      {loading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing here"
          description="No registrations in this state right now."
        />
      ) : (
        <Stack spacing={1.5}>
          {rows.map((row) => (
            <Card key={row.registrationId} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>
                      {row.fullName || row.email}
                    </Typography>
                    {row.source === 'self_registered' && (
                      <Chip size="small" label="Self-registered" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {row.email}
                    {row.company ? ` · ${row.company}` : ''}
                  </Typography>
                  {row.registeredAt && (
                    <Typography variant="caption" color="text.secondary">
                      Registered {formatShortDay(row.registeredAt)}
                    </Typography>
                  )}
                  {Object.keys(row.values ?? {}).length > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 0.5 }}
                    >
                      {Object.entries(row.values)
                        .map(([k, v]) => `${k}: ${String(v)}`)
                        .join(' · ')}
                    </Typography>
                  )}
                </Box>
                {status === 'pending_approval' && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CheckRoundedIcon />}
                      disabled={busy === row.registrationId}
                      onClick={() => act(row, 'approve')}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<CloseRoundedIcon />}
                      disabled={busy === row.registrationId}
                      onClick={() => act(row, 'reject')}
                    >
                      Reject
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  )
}
